import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import type * as MonacoType from 'monaco-editor';
import { FormCodeEditorLanguageEnum } from './form-code-editor-language.enum';

declare global {
  interface Window {
    monaco: typeof MonacoType;
  }
}

@Component({
  selector: 'flow-form-code-editor',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './form-code-editor.component.html',
  styleUrls: ['./form-code-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormCodeEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly control = inject(NgControl, { optional: true, self: true });

  @ViewChild('editorContainer') editorContainer!: ElementRef<HTMLDivElement>;

  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() language: FormCodeEditorLanguageEnum = FormCodeEditorLanguageEnum.Json;
  @Input() theme: 'vs' | 'vs-dark' | 'hc-black' = 'vs-dark';
  @Input() readOnly: boolean = false;
  @Input() height: string = '400px';
  @Input() editorOptions: MonacoType.editor.IStandaloneEditorConstructionOptions = {};

  @Output() codeChange = new EventEmitter<string>();

  private editor: MonacoType.editor.IStandaloneCodeEditor | null = null;
  private markerSubscription: MonacoType.IDisposable | null = null;
  private readonly destroy$ = new Subject<void>();
  private visibilityObserver: IntersectionObserver | null = null;

  value: string = '';
  isDisabled: boolean = false;

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    // Defer initialization until the container is actually visible.
    // The tabs component hides inactive panes with [hidden] (display:none), so
    // ngAfterViewInit fires with a 0x0 container — Monaco would render blank.
    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.visibilityObserver!.disconnect();
        this.visibilityObserver = null;
        this.loadAndCreateEditor();
      }
    });
    this.visibilityObserver.observe(this.editorContainer.nativeElement);
  }

  private loadAndCreateEditor(): void {
    const win = window as any;
    if (win.monaco) {
      this.createEditor();
    } else {
      win.require.config({ paths: { vs: 'assets/monaco/vs' } });
      win.require(['vs/editor/editor.main'], () => {
        this.ngZone.run(() => this.createEditor());
      });
    }
  }

  private createEditor(): void {
    this.ngZone.runOutsideAngular(() => {
      this.editor = window.monaco.editor.create(this.editorContainer.nativeElement, {
        value: this.value,
        language: this.language,
        theme: this.theme,
        readOnly: this.readOnly || this.isDisabled,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 13,
        wordWrap: 'on',
        ...this.editorOptions,
      });

      this.editor.onDidPaste(() => {
        this.editor!.getAction('editor.action.formatDocument')?.run();
      });

      this.editor.onDidBlurEditorText(() => {
        this.editor!.getAction('editor.action.formatDocument')?.run();
      });

      this.editor.onDidChangeModelContent(() => {
        const newValue = this.editor!.getValue();
        this.ngZone.run(() => {
          this.onChange(newValue);
          this.onTouched();
          this.codeChange.emit(newValue);
        });
      });

      const model = this.editor.getModel();
      if (model) {
        this.markerSubscription = window.monaco.editor.onDidChangeMarkers((uris) => {
          const modelUri = model.uri.toString();
          if (!uris.some(u => u.toString() === modelUri)) return;
          const markers = window.monaco.editor.getModelMarkers({ resource: model.uri });
          const hasErrors = markers.some(m => m.severity === window.monaco.MarkerSeverity.Error);
          this.ngZone.run(() => this.syncMonacoErrorsToControl(hasErrors));
        });
      }
    });
  }

  private syncMonacoErrorsToControl(hasErrors: boolean): void {
    if (!this.control?.control) return;
    if (hasErrors) {
      this.control.control.setErrors({ monacoErrors: true });
    } else {
      const errors = { ...this.control.control.errors };
      delete errors['monacoErrors'];
      this.control.control.setErrors(Object.keys(errors).length ? errors : null);
    }
    this.cdr.markForCheck();
  }

  writeValue(value: string): void {
    this.value = value ?? '';
    if (this.editor) {
      const model = this.editor.getModel();
      if (model && model.getValue() !== this.value) {
        model.setValue(this.value);
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.editor?.updateOptions({ readOnly: isDisabled || this.readOnly });
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.visibilityObserver?.disconnect();
    this.markerSubscription?.dispose();
    this.markerSubscription = null;
    this.destroy$.next();
    this.destroy$.complete();
    this.editor?.getModel()?.dispose();
    this.editor?.dispose();
    this.editor = null;
  }
}
